import { useState, useEffect, useCallback } from 'react';
import {
  History,
  Loader2,
  Check,
  XCircle,
  Trash2,
  Clock,
  RotateCcw,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Search,
  Info,
} from 'lucide-react';
import { useAdminNotifications } from '@/hooks/useNotifications';
import {
  NotificationHistoryItem,
  NotificationHistoryResponse,
  NotificationType,
  NotificationTypeConfig,
  RecipientDetail,
} from '@/types/notification.types';
import { notificationService } from '@/services/notification.service';
import { format } from 'date-fns';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import Pagination from '@/components/ui/Pagination';
import Modal from '@/components/ui/Modal';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { TableSkeleton, SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { PushDetailsModal } from '../PushDetailsModal';

// Compact TTL formatter for the inline pill on each history row.
// Matches NotificationItem's formatting so both surfaces describe TTL
// consistently (60s/min → e.g. "3d", "1h", "15m").
function formatTtlPill(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

const historyTypeOptions: SelectOption[] = [
  { label: 'All types', value: '' },
  ...Object.entries(NotificationTypeConfig).map(([type, config]) => ({
    label: config.label,
    value: type,
  })),
];

const historyStatusOptions: SelectOption[] = [
  { label: 'All statuses', value: '' },
  { label: 'Sent', value: 'SENT' },
  { label: 'Scheduled', value: 'SCHEDULED' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Failed', value: 'FAILED' },
];

interface NotificationHistoryProps {
  onActionComplete?: () => void;
  refreshKey?: number;
}

export function NotificationHistory({ onActionComplete, refreshKey }: NotificationHistoryProps) {
  const { getNotificationHistory, cancelScheduledNotification, retryNotification } = useAdminNotifications();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<NotificationHistoryResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, DEFAULT_DEBOUNCE_DELAY);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  // Confirm action modal
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    actionLabel: string;
    actionColor: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', actionLabel: '', actionColor: '', onConfirm: () => {} });

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  // Message detail modal
  const [messageModal, setMessageModal] = useState<{ title: string; message: string } | null>(null);

  // Push details modal (icon + TTL) — opened by the small Info button on
  // history rows that include the PUSH channel.
  const [pushDetailsModal, setPushDetailsModal] = useState<{
    title: string;
    iconUrl: string | null | undefined;
    ttl: number | null;
  } | null>(null);

  // Recipients modal
  const [recipientModal, setRecipientModal] = useState<{
    isOpen: boolean;
    title: string;
    recipients: RecipientDetail[];
    isLoading: boolean;
    noReadTracking: boolean;
  }>({ isOpen: false, title: '', recipients: [], isLoading: false, noReadTracking: false });

  const openRecipientsModal = async (notification_id: string, title: string, channels: string[]) => {
    // Email and push don't track reads — show "Delivered At" instead.
    const noReadTracking = channels.length > 0 && channels.every(c => c === 'EMAIL' || c === 'PUSH');
    setRecipientModal({ isOpen: true, title, recipients: [], isLoading: true, noReadTracking });
    const result = await notificationService.getNotificationRecipients(notification_id);
    setRecipientModal(prev => ({
      ...prev,
      recipients: result?.recipients || [],
      isLoading: false,
    }));
  };

  const closeRecipientsModal = () => {
    setRecipientModal({ isOpen: false, title: '', recipients: [], isLoading: false, noReadTracking: false });
  };

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    const response = await getNotificationHistory({
      page: currentPage,
      limit: pageSize,
      type: typeFilter || undefined,
      search: debouncedSearch.trim() || undefined,
      history_status: (statusFilter as 'SENT' | 'SCHEDULED' | 'CANCELLED' | 'FAILED') || undefined,
    });
    setData(response);
    setIsLoading(false);
  }, [getNotificationHistory, currentPage, pageSize, typeFilter, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Refetch when parent signals a new notification was sent
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchHistory();
    }
  }, [refreshKey]);

  const handleCancel = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Revoke Notification',
      message: 'Are you sure you want to revoke this scheduled notification? This action cannot be undone.',
      actionLabel: 'Revoke',
      actionColor: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        closeConfirmModal();
        setCancellingId(id);
        const success = await cancelScheduledNotification(id);
        setCancellingId(null);
        if (success) {
          fetchHistory();
          onActionComplete?.();
        }
      },
    });
  };

  const handleRetry = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Retry Notification',
      message: 'Are you sure you want to retry delivery for failed/pending recipients?',
      actionLabel: 'Retry',
      actionColor: 'bg-orange-600 hover:bg-orange-700',
      onConfirm: async () => {
        closeConfirmModal();
        setRetryingId(id);
        const success = await retryNotification(id);
        setRetryingId(null);
        if (success) {
          fetchHistory();
          onActionComplete?.();
        }
      },
    });
  };

  // Server-side handles type + search + status + pagination
  const rows = data?.data || [];
  const totalPages = data ? Math.ceil(data.meta_data.total / pageSize) : 0;

  const getStatusBadge = (item: NotificationHistoryItem) => {
    const isScheduled = item.scheduled_at && new Date(item.scheduled_at) > new Date();

    if (isScheduled) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
          <Clock className="w-3 h-3" />
          Scheduled
        </span>
      );
    }

    if (item.failed_count > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
          <XCircle className="w-3 h-3" />
          {item.failed_count} failed
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
        <Check className="w-3 h-3" />
        Sent
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-xl">
              <History className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Notification History
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, message, or creator..."
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setCurrentPage(1); }}
                className="pl-9 pr-3 py-2.5 w-80 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent"
              />
            </div>
            {/* Type filter */}
            <div className="w-44">
              <SimpleSelect
                options={historyTypeOptions}
                value={typeFilter}
                onChange={(val) => {
                  setTypeFilter((val as string) ?? '');
                  setCurrentPage(1);
                }}
                placeholder="All types"
                isSearchable={false}
                isClearable={false}
              />
            </div>
            {/* Status filter */}
            <div className="w-44">
              <SimpleSelect
                options={historyStatusOptions}
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter((val as string) ?? '');
                  setCurrentPage(1);
                }}
                placeholder="All statuses"
                isSearchable={false}
                isClearable={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {isLoading && !data ? (
          <div className="p-4">
            <TableSkeleton rows={pageSize} columns={7} />
          </div>
        ) : !data || rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <History className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500">No notifications found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Notification
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Channel
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Recipients
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((item) => {
                const typeConfig = NotificationTypeConfig[item.type as NotificationType];
                const isScheduled = item.scheduled_at && new Date(item.scheduled_at) > new Date();

                return (
                  <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 truncate">
                            {item.title}
                          </p>
                          {/* Push-only enrichment: a small icon thumbnail
                              gives an at-a-glance preview of the push image,
                              the TTL pill surfaces the delivery window, and
                              the Info button opens the full details modal.
                              Falls back to OneSignal's default 3-day TTL when
                              push_options.ttl wasn't set on send. */}
                          {item.channels.includes('PUSH' as any) && (() => {
                            const effective_ttl =
                              typeof item.ttl === 'number' && item.ttl > 0 ? item.ttl : 259200;
                            return (
                              <>
                                {item.icon_url && (
                                  <img
                                    src={item.icon_url}
                                    alt="push icon"
                                    className="w-5 h-5 rounded object-cover border border-gray-200 flex-shrink-0"
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                )}
                                <span
                                  className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-primary-50 text-primary-700 rounded"
                                  title={`Push TTL: ${effective_ttl}s`}
                                >
                                  TTL {formatTtlPill(effective_ttl)}
                                </span>
                                <button
                                  onClick={() =>
                                    setPushDetailsModal({
                                      title: item.title,
                                      iconUrl: item.icon_url,
                                      ttl: effective_ttl,
                                    })
                                  }
                                  className="flex-shrink-0 p-0.5 text-gray-400 hover:text-primary-600 transition-colors"
                                  title="View push details"
                                >
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              </>
                            );
                          })()}
                        </div>
                        <div className="flex items-center gap-1">
                          <p className="text-sm text-gray-500 truncate" style={{ maxWidth: 220 }}>
                            {item.message.length > 80 ? item.message.slice(0, 80) + '...' : item.message}
                          </p>
                          {item.message.length > 80 && (
                            <button
                              onClick={() => setMessageModal({ title: item.title, message: item.message })}
                              className="flex-shrink-0 p-0.5 text-gray-400 hover:text-primary-600 transition-colors"
                              title="View full message"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          By <span className="font-medium text-gray-600">{item.created_by_name}</span>
                          {item.created_by_email && <span className="text-gray-400"> · {item.created_by_email}</span>}
                          {item.created_by_employee_id && <span className="text-gray-400"> · {item.created_by_employee_id}</span>}
                        </p>
                        {item.action_url && (
                          <a
                            href={item.action_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 mt-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-3 h-3" /> Action Link
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        {typeConfig?.label || item.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap items-center gap-1">
                        {item.channels.map((channel) => (
                          <span
                            key={channel}
                            className="px-2 py-0.5 text-xs font-medium bg-brand-blue-50 text-brand-blue-600 rounded"
                          >
                            {channel === 'IN_APP' ? 'In-App' : channel === 'EMAIL' ? 'Email' : channel === 'PUSH' ? 'Push' : channel}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{item.recipients_count} recipient{item.recipients_count !== 1 ? 's' : ''}</p>
                          <button
                            onClick={() => openRecipientsModal(item._id, item.title, item.channels)}
                            className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                            title="View all recipients"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          {isScheduled ? (
                            <span className="text-yellow-600">{item.recipients_count} scheduled</span>
                          ) : (
                            <>
                              <span className="text-green-600">{item.delivered_count} delivered</span>
                              {(item.recipients_count - item.delivered_count - item.failed_count) > 0 && (
                                <span className="text-yellow-600">{item.recipients_count - item.delivered_count - item.failed_count} pending</span>
                              )}
                              {item.failed_count > 0 && <span className="text-red-600">{item.failed_count} failed</span>}
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const pending_count = item.recipients_count - item.delivered_count - item.failed_count;
                        if (item.is_cancelled) return (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                            <XCircle className="w-3 h-3" /> Cancelled
                          </span>
                        );
                        if (isScheduled) return (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
                            <Clock className="w-3 h-3" /> Scheduled
                          </span>
                        );
                        if (pending_count > 0) return (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        );
                        if (item.failed_count > 0 && item.delivered_count === 0) return (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            <XCircle className="w-3 h-3" /> Failed
                          </span>
                        );
                        if (item.delivered_count > 0) return (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                            <Check className="w-3 h-3" /> Sent
                          </span>
                        );
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {isScheduled ? (
                          <>
                            <p className="text-gray-900">
                              {format(new Date(item.scheduled_at!), 'MMM d, yyyy')}
                            </p>
                            <p className="text-xs text-yellow-600 font-medium">
                              Scheduled {format(new Date(item.scheduled_at!), 'h:mm a')}
                            </p>
                          </>
                        ) : (
                          <>
                            <p>{format(new Date(item.created_at), 'MMM d, yyyy')}</p>
                            <p className="text-xs text-gray-500">
                              {format(new Date(item.created_at), 'h:mm a')}
                            </p>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {isScheduled && !item.is_cancelled ? (
                        <button
                          onClick={() => handleCancel(item._id)}
                          disabled={cancellingId === item._id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Revoke scheduled notification"
                        >
                          {cancellingId === item._id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <><Trash2 className="w-3.5 h-3.5" /> Revoke</>
                          )}
                        </button>
                      ) : (item.failed_count > 0 || (item.recipients_count - item.delivered_count - item.failed_count) > 0) && !item.is_cancelled && !isScheduled ? (
                        <button
                          onClick={() => handleRetry(item._id)}
                          disabled={retryingId === item._id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Retry failed/pending"
                        >
                          {retryingId === item._id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <><RotateCcw className="w-3.5 h-3.5" /> Retry</>
                          )}
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data && totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={data.meta_data.total}
          itemsPerPage={pageSize}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Recipients Modal */}
      <Modal
        isOpen={recipientModal.isOpen}
        onClose={closeRecipientsModal}
        title="Recipients"
        subtitle={recipientModal.title}
        size="xl"
      >
        {recipientModal.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : recipientModal.recipients.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No recipients found</p>
        ) : (
          <div>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ fontSize: 13, minWidth: 720 }}>
              <thead style={{ background: '#f8f8f8' }} className="sticky top-0 z-10">
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', color: '#666', width: 40 }}>#</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', color: '#666' }}>Name</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', color: '#666' }}>Email</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', color: '#666' }}>Emp ID</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', color: '#666' }}>Status</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', color: '#666' }}>{recipientModal.noReadTracking ? 'Delivered At' : 'Read At'}</th>
                </tr>
              </thead>
              <tbody>
                {recipientModal.recipients.map((r, i) => {
                  const is_read = r.status === 'READ';
                  return (
                    <tr key={r.user_id} style={{ borderTop: '1px solid #f0f0f0' }} className="hover:bg-gray-50">
                      <td style={{ padding: '8px 12px', color: '#999' }}>{i + 1}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 500, color: '#1d1d1f' }}>{r.user_name}</td>
                      <td style={{ padding: '8px 12px', color: '#444' }}>{r.user_email}</td>
                      <td style={{ padding: '8px 12px', color: '#444' }}>{r.employee_id}</td>
                      <td style={{ padding: '8px 12px' }}>
                        {r.status === 'READ' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-50 text-green-700">
                            <CheckCircle className="w-3 h-3" /> Read
                          </span>
                        ) : r.status === 'DELIVERED' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700">
                            <CheckCircle className="w-3 h-3" /> Delivered
                          </span>
                        ) : r.status === 'FAILED' ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-50 text-red-700"
                            title={
                              r.failure_reason === 'PREFERENCE_DISABLED'
                                ? 'User has this notification channel disabled in their preferences'
                                : r.failure_reason === 'DELIVERY_ERROR'
                                ? 'Delivery failed due to a system error'
                                : undefined
                            }
                          >
                            <AlertCircle className="w-3 h-3" /> Failed
                            {r.failure_reason === 'PREFERENCE_DISABLED' && (
                              <span className="ml-1 font-normal text-red-600">· preference off</span>
                            )}
                            {r.failure_reason === 'DELIVERY_ERROR' && (
                              <span className="ml-1 font-normal text-red-600">· delivery error</span>
                            )}
                          </span>
                        ) : r.status === 'CANCELLED' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                            <XCircle className="w-3 h-3" /> Cancelled
                          </span>
                        ) : r.status === 'SCHEDULED' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">
                            <Clock className="w-3 h-3" /> Scheduled
                          </span>
                        ) : r.status === 'PENDING' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-50 text-yellow-700">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700">
                            <Clock className="w-3 h-3" /> Unread
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', color: '#444', fontSize: 12 }}>
                        {recipientModal.noReadTracking
                          ? (r.status === 'DELIVERED' && r.delivered_at
                              ? format(new Date(r.delivered_at), 'MMM d, yyyy h:mm a')
                              : '—')
                          : (r.read_at ? format(new Date(r.read_at), 'MMM d, yyyy h:mm a') : '—')
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100 bg-gray-50 flex gap-4 flex-wrap">
              <span>{recipientModal.recipients.length} total</span>
              {recipientModal.recipients.filter(r => r.status === 'READ').length > 0 && (
                <span className="text-green-600">{recipientModal.recipients.filter(r => r.status === 'READ').length} read</span>
              )}
              {recipientModal.recipients.filter(r => r.status === 'UNREAD').length > 0 && (
                <span className="text-amber-600">{recipientModal.recipients.filter(r => r.status === 'UNREAD').length} unread</span>
              )}
              {recipientModal.recipients.filter(r => r.status === 'DELIVERED').length > 0 && (
                <span className="text-blue-600">{recipientModal.recipients.filter(r => r.status === 'DELIVERED').length} delivered</span>
              )}
              {recipientModal.recipients.filter(r => r.status === 'PENDING').length > 0 && (
                <span className="text-yellow-600">{recipientModal.recipients.filter(r => r.status === 'PENDING').length} pending</span>
              )}
              {recipientModal.recipients.filter(r => r.status === 'SCHEDULED').length > 0 && (
                <span className="text-yellow-600">{recipientModal.recipients.filter(r => r.status === 'SCHEDULED').length} scheduled</span>
              )}
              {recipientModal.recipients.filter(r => r.status === 'CANCELLED').length > 0 && (
                <span className="text-gray-600">{recipientModal.recipients.filter(r => r.status === 'CANCELLED').length} cancelled</span>
              )}
              {recipientModal.recipients.filter(r => r.status === 'FAILED').length > 0 && (
                <span className="text-red-600">{recipientModal.recipients.filter(r => r.status === 'FAILED').length} failed</span>
              )}
            </div>
          </div>
        )}
      </Modal>
      {/* Message Detail Modal */}
      <Modal
        isOpen={!!messageModal}
        onClose={() => setMessageModal(null)}
        title={messageModal?.title || 'Message'}
        size="md"
      >
        <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
          {messageModal?.message}
        </p>
      </Modal>
      {/* Push Details Modal — opened from the Info button on push rows */}
      <PushDetailsModal
        isOpen={!!pushDetailsModal}
        onClose={() => setPushDetailsModal(null)}
        title={pushDetailsModal?.title || ''}
        iconUrl={pushDetailsModal?.iconUrl}
        ttl={pushDetailsModal?.ttl ?? null}
      />
      {/* Confirm Action Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        title={confirmModal.title}
        size="sm"
      >
        <p className="text-sm text-gray-600 mb-6">{confirmModal.message}</p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={closeConfirmModal}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirmModal.onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${confirmModal.actionColor}`}
          >
            {confirmModal.actionLabel}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default NotificationHistory;
